import * as React from "react";
import { Button } from "@repo/ui";
import {
  User,
  Building2,
  ListFilter,
  CircleDot,
  CheckCircle2,
  UserRound,
} from "lucide-react";
import type { ReminderFilters } from "@/hooks/use-reminders";
import { getPriorityColor } from "@/lib/utils/reminder-format";

interface ReminderFiltersBarProps {
  filters: ReminderFilters;
  onFiltersChange: (filters: ReminderFilters) => void;
  overdueCount: number;
  dueTodayCount: number;
  activeCount: number;
}

export function ReminderFiltersBar({
  filters,
  onFiltersChange,
  overdueCount,
  dueTodayCount,
  activeCount,
}: ReminderFiltersBarProps) {
  const visibilityOptions = [
    { value: "all" as const, label: "All", icon: ListFilter },
    { value: "personal" as const, label: "My Reminders", icon: User },
    { value: "pharmacy" as const, label: "Pharmacy", icon: Building2 },
  ];

  const statusOptions = [
    { value: "active" as const, label: "Active", icon: CircleDot },
    { value: "completed" as const, label: "Completed", icon: CheckCircle2 },
    { value: "all" as const, label: "All", icon: ListFilter },
  ];

  const priorityOptions = [
    { value: "all" as const, label: "All Priorities" },
    { value: "standard" as const, label: "Standard" },
    { value: "urgent" as const, label: "Urgent" },
  ];

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        {/* Visibility Filter */}
        <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
          {visibilityOptions.map((option) => {
            const Icon = option.icon;
            const isActive = (filters.visibility || "all") === option.value;
            return (
              <Button
                key={option.value}
                variant={isActive ? "default" : "ghost"}
                size="sm"
                className="gap-2"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    visibility: option.value,
                  })
                }
              >
                <Icon className="h-4 w-4" />
                {option.label}
              </Button>
            );
          })}
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
          {statusOptions.map((option) => {
            const Icon = option.icon;
            const isActive = (filters.status || "active") === option.value;
            return (
              <Button
                key={option.value}
                data-testid={
                  option.value === "completed"
                    ? "reminder-filter-status-completed"
                    : undefined
                }
                variant={isActive ? "default" : "ghost"}
                size="sm"
                className="gap-2"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    status: option.value,
                  })
                }
              >
                <Icon className="h-4 w-4" />
                {option.label}
              </Button>
            );
          })}
        </div>

        {/* Priority Filter */}
        <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
          {priorityOptions.map((option) => {
            const isActive = (filters.priority || "all") === option.value;
            return (
              <Button
                key={option.value}
                variant={isActive ? "default" : "ghost"}
                size="sm"
                className="gap-2"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    priority: option.value,
                  })
                }
              >
                {option.value !== "all" && !isActive && (
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${getPriorityColor(option.value)}`}
                  />
                )}
                {option.label}
              </Button>
            );
          })}
        </div>

        {/* Patient Reminders Toggle */}
        <Button
          variant={filters.patientLinked ? "default" : "outline"}
          size="sm"
          className="gap-2"
          onClick={() =>
            onFiltersChange({
              ...filters,
              patientLinked: !filters.patientLinked,
            })
          }
        >
          <UserRound className="h-4 w-4" />
          Patient Reminders
        </Button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {overdueCount > 0 && (
          <span className="text-destructive font-medium">
            {overdueCount} overdue
          </span>
        )}
        {overdueCount > 0 && dueTodayCount > 0 && (
          <span className="text-border">&middot;</span>
        )}
        {dueTodayCount > 0 && (
          <span className="text-amber-600 font-medium">
            {dueTodayCount} due today
          </span>
        )}
        {(overdueCount > 0 || dueTodayCount > 0) && (
          <span className="text-border">&middot;</span>
        )}
        <span>{activeCount} active</span>
      </div>
    </div>
  );
}

export {
  getPriorityColor,
  getPriorityLabel,
} from "@/lib/utils/reminder-format";
